<script setup lang="ts">
import type { NavItem } from '@nuxt/content/types'
import { Constants } from '@yescapa-dev/ysc-api-js/legacy'
import type { SearchProductsQueryParameters } from '@yescapa-dev/ysc-api-js/legacy'
import type { CamperTypeContent } from './index.vue'
import type { LocationQueryRaw, RouteLocationRaw } from '#vue-router'
import { YSC_API_ERROR, YSC_API_SEARCH_ERROR } from '~/utils/error/YscErrorClasses'
import { ACLASS_1, CONVERTEDVAN_WOOD_FOREST, USER_AND_VAN } from '~/constants/pictures'

const route = useRoute()

type LandingSeatsVehicleType = 'motorhome' | 'van' | 'campervan'

const slug = computed<LandingSeatsVehicleType>(() => paramValueToString(route.params.slug) as LandingSeatsVehicleType)

const seats = computed(() => paramValueToNumber(route.params.seats))

const { $errorManager } = useErrorManager()
const { $api } = useYscApi()

const { t } = useI18n()

const { stats } = useStatsStore()

definePageMeta({
  validate: async (route) => {
    const { params } = route
    const slug = paramValueToString(params.slug)
    return ['motorhome', 'campervan', 'van'].includes(slug)
  },
})

const findOneLocalizedContentWithFallback = useFindOneLocalizedContentWithFallback()

type LandingSeatDefinition = {
  searchQuery: SearchProductsQueryParameters
  image: string
  linkLabel: string
  translations: {
    searchTitle: string
    howItWorksTitle: string
    howItWorksSubtitle: string
    countriesTitle: string
    linksTitle: string
    linksDescription: string
    searchDescription: string
  }
}

const definitions = computed<Record<LandingSeatsVehicleType, LandingSeatDefinition>>(() => {
  const i18nP = { seats_number: seats.value }
  return {
    motorhome: {
      searchQuery: {
        types: `${Constants.PRODUCTS.TYPES.ACLASS},${Constants.PRODUCTS.TYPES.LOWPROFILE},${Constants.PRODUCTS.TYPES.COACHBUILT}`,
      },
      image: twicpicsPath(ACLASS_1, { isLibrary: true }),
      linkLabel: t('commons.rent_a_camper.default'),
      translations: {
        searchTitle: t('pages.camper_types_seats.search_title_motorhome_dynamic', i18nP),
        howItWorksTitle: t('pages.camper_types_seats.how_it_works_title_motorhome_dynamic', i18nP),
        howItWorksSubtitle: t('pages.camper_types_seats.how_it_works_subtitle_motorhome_dynamic', i18nP),
        countriesTitle: t('pages.camper_types_seats.landing_countries_links_title_motorhome_dynamic', i18nP),
        linksTitle: t('pages.camper_types_seats.landing_links_title_motorhome'),
        linksDescription: t('pages.camper_types_seats.landing_links_description_motorhome'),
        searchDescription: t('pages.camper_types_seats.search_description_motorhome_dynamic', i18nP),
      },
    },
    van: {
      searchQuery: {
        types: `${Constants.PRODUCTS.TYPES.VAN}`,
      },
      image: twicpicsPath(USER_AND_VAN, { isLibrary: true }),
      linkLabel: t('commons.rent_a_camper.van'),
      translations: {
        searchTitle: t('pages.camper_types_seats.search_title_van_dynamic', i18nP),
        howItWorksTitle: t('pages.camper_types_seats.how_it_works_title_van_dynamic', i18nP),
        howItWorksSubtitle: t('pages.camper_types_seats.how_it_works_subtitle_van_dynamic', i18nP),
        countriesTitle: t('pages.camper_types_seats.landing_countries_links_title_van_dynamic', i18nP),
        linksTitle: t('pages.camper_types_seats.landing_links_title_van'),
        linksDescription: t('pages.camper_types_seats.landing_links_description_van'),
        searchDescription: t('pages.camper_types_seats.search_description_van_dynamic', i18nP),
      },
    },
    campervan: {
      searchQuery: {
        types: `${Constants.PRODUCTS.TYPES.CAMPERVAN}`,
      },
      image: twicpicsPath(CONVERTEDVAN_WOOD_FOREST, { isLibrary: true }),
      linkLabel: t('commons.rent_a_camper.campervan'),
      translations: {
        searchTitle: t('pages.camper_types_seats.search_title_campervan_dynamic', i18nP),
        howItWorksTitle: t('pages.camper_types_seats.how_it_works_title_campervan_dynamic', i18nP),
        howItWorksSubtitle: t('pages.camper_types_seats.how_it_works_subtitle_campervan_dynamic', i18nP),
        countriesTitle: t('pages.camper_types_seats.landing_countries_links_title_campervan_dynamic', i18nP),
        linksTitle: t('pages.camper_types_seats.landing_links_title_campervan'),
        linksDescription: t('pages.camper_types_seats.landing_links_description_campervan'),
        searchDescription: t('pages.camper_types_seats.search_description_campervan_dynamic', i18nP),
      },
    },
  }
})

const landingDefinition = computed(() => definitions.value[slug.value])

const { data, error } = await useAsyncData(`page-camper-type-${slug.value}-${seats.value}`, async () => {
  const data = {
    content: [],
    campers: { results: [] },
  }
  if (!landingDefinition.value) {
    return data
  }

  const [contentRequest, campersRequest] = await Promise.allSettled([
    findOneLocalizedContentWithFallback<CamperTypeContent>(`camper-type/${slug.value}/${seats.value}-seats`),
    $api.search.searchProducts({
      ...definitions.value[slug.value].searchQuery,
      seatbelts: seats.value || 0,
      page_size: 6,
    }),
  ])

  if (contentRequest.status === 'rejected') {
    $errorManager({ e: contentRequest.reason })
    throw createError({ statusCode: 404 })
  }
  if (campersRequest.status === 'rejected') {
    $errorManager({ e: campersRequest.reason, name: YSC_API_SEARCH_ERROR })
  }

  return {
    content: contentRequest.value,
    campers: campersRequest.status === 'rejected' ? { results: [] } : campersRequest.value,
  }
}, {
  watch: [landingDefinition],
})

if (error.value) {
  throw createError(error.value)
}

const { data: landingSeatsLinks } = await useAsyncData(`page-camper-type-${slug.value}-${seats.value}-navigation`, async () => {
  const getLanguagesToFetch = useGetLanguagesToFetch()
  const languagesToFetch = getLanguagesToFetch()
  const lang = languagesToFetch.shift() as string
  const navigationItems = await fetchContentNavigation(queryLocalizedContent(`camper-type`, lang))
  const extractPaths = (data: NavItem[]) => {
    const result: {
      name: string
      nxt_url: string
    }[] = []

    const recursiveExtract = (items: NavItem[]) => {
      items.forEach((item) => {
        if (item.children) {
          recursiveExtract(item.children)
        }
        else {
          result.push({ name: item.title, nxt_url: item._path })
        }
      })
    }

    recursiveExtract(data)
    return result
  }
  return extractPaths(navigationItems)
})

const { data: countries } = await useAsyncData(`page-camper-type-${slug.value}-countries`, async () => {
  try {
    const response = await $api.geolocation.getPlaces({ place_type: 'COUNTRY', allowed_on_home: true })
    return response.results
  }
  catch (e) {
    if (e instanceof Error) {
      throw $errorManager({ e, name: YSC_API_ERROR })
    }
  }
})

const safeCampers = computed(() => data.value?.campers ? data.value?.campers : { results: [] })
const searchQuery = computed(() => {
  if (!landingDefinition.value) {
    return null
  }
  const query = landingDefinition.value.searchQuery as LocationQueryRaw
  query.seatbelts = seats.value
  return query
})
const searchLink = computed((): RouteLocationRaw | undefined => searchQuery.value ? { name: 's', query: searchQuery.value } : undefined)
const vehicleTypeString = computed(() => slug.value === 'motorhome' ? useVehicleType({ type: 'default' })?.toLowerCase() : useVehicleType({ type: slug.value })?.toLowerCase())

const title = computed(() => t('pages.camper_types_seats.title_seo_dynamic', {
  vehicle_type_string: vehicleTypeString.value,
  seats_number: seats.value,
}))

const seoDescription = computed<Record<LandingSeatsVehicleType, Record<number, string>>>(() => ({
  motorhome: {
    2: t('pages.camper_types_seats.meta_description_motorhome_2_seats'),
    3: t('pages.camper_types_seats.meta_description_motorhome_3_seats'),
    4: t('pages.camper_types_seats.meta_description_motorhome_4_seats'),
    5: t('pages.camper_types_seats.meta_description_motorhome_5_seats'),
    6: t('pages.camper_types_seats.meta_description_motorhome_6_seats'),
    7: t('pages.camper_types_seats.meta_description_motorhome_7_seats'),
    8: t('pages.camper_types_seats.meta_description_motorhome_8_seats'),
  },
  van: {
    2: t('pages.camper_types_seats.meta_description_van_2_seats'),
    3: t('pages.camper_types_seats.meta_description_van_3_seats'),
    4: t('pages.camper_types_seats.meta_description_van_4_seats'),
  },
  campervan: {
    2: t('pages.camper_types_seats.meta_description_campervan_2_seats'),
    3: t('pages.camper_types_seats.meta_description_campervan_3_seats'),
    4: t('pages.camper_types_seats.meta_description_campervan_4_seats'),
    5: t('pages.camper_types_seats.meta_description_campervan_5_seats'),
  },
}))

useSeoText({ title, description: seats.value ? seoDescription.value[slug.value][seats.value] : '' })
</script>

<template>
  <div>
    <AppPageHero
      :bg-image="landingDefinition.image"
      :extra-query="searchQuery"
    >
      <template #title-big>
        {{ $t('pages.camper_types_seats.title') }}
      </template>
      <template #title-seo>
        {{ title }}
      </template>
    </AppPageHero>
    <AppPageHeroBanner
      :average="stats.average"
      :count="stats.count"
    />

    <AppPageSection v-if="landingDefinition.searchQuery">
      <div class="flex flex-col items-center gap-8">
        <AppSectionCampers
          :campers="safeCampers.results"
          class="self-stretch"
        >
          <template #title>
            {{ landingDefinition.translations.searchTitle }}
          </template>
          <template #description>
            {{ landingDefinition.translations.searchDescription }}
          </template>
          <template #action>
            <NuxtLink
              :to="searchLink"
              class="btn btn-secondary"
            >
              {{ landingDefinition.linkLabel }}
            </NuxtLink>
          </template>
        </AppSectionCampers>
      </div>
    </AppPageSection>

    <AppPageSection v-if="data?.content">
      <div class="flex items-center justify-between space-x-8">
        <ContentRenderer
          class="prose lg:prose-lg"
          :value="data.content"
        />
        <div class="hidden lg:block w-full">
          <TwicImg
            :alt="title"
            mode="contain"
            ratio="16/9"
            :src="twicpicsPath(data.content.picture)"
          />
        </div>
      </div>
    </AppPageSection>

    <AppHowItWorksSection user-type="guest">
      <template #title>
        {{ landingDefinition.translations.howItWorksTitle }}
      </template>
      <template #subtitle>
        {{ landingDefinition.translations.howItWorksSubtitle }}
      </template>
    </AppHowItWorksSection>

    <div class="container py-24 space-y-14">
      <AppLandingLinks
        v-if="countries"
        :links="countries"
      >
        <template #title>
          {{ landingDefinition.translations.countriesTitle }}
        </template>
        <template #description>
          {{ $t('pages.index.next_roadtrip_is_waiting') }}
        </template>
        <template #links="{ links }">
          <AppLandingLink
            v-for="country in links"
            :key="country.name"
            :to="country.nxt_url"
          >
            {{ country.name }}
          </AppLandingLink>
        </template>
      </AppLandingLinks>

      <AppLandingLinks
        v-if="landingSeatsLinks"
        :links="landingSeatsLinks"
      >
        <template #title>
          {{ landingDefinition.translations.linksTitle }}
        </template>
        <template #description>
          {{ landingDefinition.translations.linksDescription }}
        </template>
        <template #links="{ links }">
          <AppLandingLink
            v-for="country in links"
            :key="country.name"
            :to="country.nxt_url"
          >
            {{ country.name }}
          </AppLandingLink>
        </template>
      </AppLandingLinks>
    </div>
  </div>
</template>
